import { PDFPageProxy, RenderTask } from 'pdfjs-dist/legacy/build/pdf';

class PdfPage {
  private canvas: HTMLCanvasElement | null = null;

  private canvasParent: HTMLDivElement;

  private context: CanvasRenderingContext2D | null = null;

  public isDirty = true;

  public pageNum: number;

  public pdfPage: PDFPageProxy;

  public renderTask: RenderTask | null = null;

  public scale = 1.0;

  constructor(pageNum: number, pdfPage: PDFPageProxy, canvasParent: HTMLDivElement) {
    this.pageNum = pageNum;
    this.pdfPage = pdfPage;
    this.canvasParent = canvasParent;
  }

  /**
  * Check if a canvas is empty, indicated by default dimensions. Is used to determine if it is the first render.
  * @param canvas - The canvas to check.
  * @returns True if the canvas is empty, false otherwise.
  */
  public canvasIsEmpty = () => {
    if (!this.canvas) return true;
    return this.canvas.width === 300 && this.canvas.height === 150;
  }

  public getCanvas = () => {
    if (!this.canvas) {
      this.initCanvas();
    }
    return this.canvas;
  }

  public getContext = () => {
    if (!this.canvas) throw new Error('Canvas not initialized. Call initCanvas() first.');
    if (!this.context) {
      this.context = this.canvas.getContext('2d', { willReadFrequently: true });
    }
    if (!this.context) throw new Error('2D context not available');
    return this.context;
  };

  public initCanvas = () => {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvasParent.appendChild(this.canvas);
    }
  }

  public renderPage = async (dpr: number) => {
    const previousTask = this.renderTask;
    if (previousTask) {
      previousTask.cancel();

      try {
        await previousTask.promise;
      } catch (err: any) {
        if (err?.name !== 'RenderingCancelledException') throw err;
      }
    }

    const viewport = this.pdfPage.getViewport({ scale: this.scale });

    const canvas = this.getCanvas();
    if (!canvas) return;
    const context = this.getContext();

    canvas.width = Math.round(viewport.width * dpr);
    canvas.height = Math.round(viewport.height * dpr);

    canvas.style.width = `${Math.round(viewport.width)}px`;
    canvas.style.height = `${Math.round(viewport.height)}px`;

    const task = this.pdfPage.render({
      canvasContext: context,
      viewport,
      transform: [dpr, 0, 0, dpr, 0, 0],
    });

    this.renderTask = task;

    try {
      await task.promise;
    } catch (err: any) {
      if (err?.name === 'RenderingCancelledException') {
        // expected if canceled
        return;
      }
      throw err;
    } finally {
      this.renderTask = null;
    }
  };
}

export default PdfPage;
